import 'components/ordering/basket-support/open-basket-stop-editing-button/open-basket-stop-editing-button.js'
import { customElement, property, state } from 'lit/decorators.js'
import { CSSResultGroup, html, LitElement, TemplateResult } from 'lit'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { indexStyles } from 'components/ordering/basket-support/open-basket-banners/open-basket-banners.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import {
  BasketIsAnonymous,
  GetBasketsByParty,
  GetUserInfoForSelectedBasket,
  GetUserInfoForSelectedBasketResponse,
  NoNameGivenDTO,
  UserNameDTO
} from '@cloudsuite/cs-ordering-defs'
import { FAKE, inject, REAL, registerDependency } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { executeOrderingRequest } from 'components/ordering/executor.js'


interface GetSelectedBasketNameAPI {
  getSelectedBasketName(shopId: number, partyId: number): Promise<NoNameGivenDTO | string>;
}

export const BASKET_NAME_API = Symbol('BASKET_NAME_API')

@registerDependency(FAKE, BASKET_NAME_API)
export class FakeGetSelectedBasketNameAPI implements GetSelectedBasketNameAPI {
  async getSelectedBasketName(shopId: number, partyId: number): Promise<NoNameGivenDTO | string> {
    shopId
    partyId
    return 'Mandje met een super mooie lange naam!'
  }
}

@registerDependency(REAL, BASKET_NAME_API)
export class RealGetSelectedBasketNameAPI implements GetSelectedBasketNameAPI {
  async getSelectedBasketName(shopId: number, partyId: number): Promise<NoNameGivenDTO | string> {
    const baskets_by_party = await executeOrderingRequest(new GetBasketsByParty(partyId, shopId))
    for (const basket of baskets_by_party.baskets) {
      if (basket.basket_id === baskets_by_party.current_basket) {
        return basket.name
      }
    }
    return new NoNameGivenDTO()
  }
}

interface GetSelectedBasketUserInfoAPI {
  getSelectedBasketUserInfo(shopId: number, partyId: number): Promise<GetUserInfoForSelectedBasketResponse | BasketIsAnonymous>;
}

export const BASKET_USER_INFO_API = Symbol('BASKET_USER_INFO_API')

@registerDependency(FAKE, BASKET_USER_INFO_API)
export class FakeGetSelectedBasketUserInfoAPI implements GetSelectedBasketUserInfoAPI {
  async getSelectedBasketUserInfo(shopId: number, partyId: number): Promise<GetUserInfoForSelectedBasketResponse | BasketIsAnonymous> {
    shopId
    partyId
    return new GetUserInfoForSelectedBasketResponse(
      new UserNameDTO('Schni', 'Schna', 'Schnappi'),
      'schnappi@das.kleine.krokodil',
      'CloudSweet'
    )
  }
}


@registerDependency(REAL, BASKET_USER_INFO_API)
export class RealGetSelectedBasketUserInfoAPI implements GetSelectedBasketUserInfoAPI {
  async getSelectedBasketUserInfo(shopId: number, partyId: number): Promise<GetUserInfoForSelectedBasketResponse | BasketIsAnonymous> {
    return await executeOrderingRequest(new GetUserInfoForSelectedBasket(partyId, shopId))
  }
}

initializeIconsByRole({
  'csc-open-basket-index-banner.title': { name: 'pen-to-square', style: 'light' },
  'csc-open-basket-top-banner.title': { name: 'pen-to-square', style: 'light' },
})

class OpenBasketBannerBase extends LitElement {

  @property({ type: Number, attribute: 'party-id' })
  party_id?: number

  @property({ type: Number, attribute: 'shop-id' })
  shop_id?: number

  @property({ type: String, attribute: 'return-url' })
  return_url?: string

  @property({ type: String, attribute: 'basket-index-url' })
  basket_index_url?: string

  @property({ type: String, attribute: 'basket-id' })
  basket_id?: string

  @inject(BASKET_USER_INFO_API)
  private getSelectedBasketUserInfoAPI!: GetSelectedBasketUserInfoAPI

  @inject(BASKET_NAME_API)
  private getSelectedBasketNameAPI!: GetSelectedBasketNameAPI

  @state()
  user_info?: GetUserInfoForSelectedBasketResponse

  @state()
  basket_name?: string | NoNameGivenDTO

  override async connectedCallback() {
    super.connectedCallback()

    if (!this.party_id) {
      console.warn('csc-open-basket-banner: missing party-id, skipping')
      return
    }

    if (!this.shop_id) {
      console.warn('csc-open-basket-banner: missing shop-id, skipping')
      return
    }

    if (!this.basket_index_url) {
      console.warn('csc-open-basket-banner: missing basket-index-url, skipping')
      return
    }

    const selectedBasketUserInfo = await this.getSelectedBasketUserInfoAPI.getSelectedBasketUserInfo(this.shop_id, this.party_id)
    if (selectedBasketUserInfo instanceof BasketIsAnonymous) {
      console.warn('csc-open-basket-banner: basket is anonymous, skipping')
      return
    }
    this.user_info = (selectedBasketUserInfo)

    this.basket_name = await this.getSelectedBasketNameAPI.getSelectedBasketName(this.shop_id, this.party_id)
  }

  getUserName(user: UserNameDTO): string {
    const middle_name = user.middle_name.trim()
    if (middle_name === '') {
      return `${user.first_name} ${user.last_name}`
    }

    return `${user.first_name} ${middle_name} ${user.last_name}`
  }

  getBasketName(name: NoNameGivenDTO | string): string {
    if (typeof name === 'string') {
      return name
    }
    return ''
  }
}

@customElement('csc-open-basket-index-banner')
export class CSCOpenBasketIndexBanner extends OpenBasketBannerBase {
  static override styles: CSSResultGroup = [hostStyle, indexStyles]

  override render(): TemplateResult {
    const userInfo = {
      name: this.user_info ? this.getUserName(this.user_info.user_name) : 'Unknown',
      email: this.user_info?.email || '',
      basketName: this.basket_name ? this.getBasketName(this.basket_name) : '',
      party_name: this.user_info?.party_name || '',
    }

    const htmlInTransTrickery = t('edit_mode_description', {
      ns: 'csc-open-basket-index-banner',
      default: 'You are now editing the basket of {{variable_user}}. Be sure to communicate any changes with the user.',
      variable_user: userInfo.name,
    }).toString().replace(userInfo.name, `<cse-text as="span" variant="heading-5">${userInfo.name}</cse-text>`)

    return html`
      <div class="container">
        <div class="heading">
          <cse-text as="h5" variant="heading-5">
            <cse-icon icon-role="csc-open-basket-index-banner.title"></cse-icon>
            ${t('edit_mode_title', {
              ns: 'csc-open-basket-index-banner',
              default: 'Edit mode'
            })}
          </cse-text>
        </div>

        <div class="description">
          <cse-text as="p" variant="body">
            ${unsafeHTML(htmlInTransTrickery)}
          </cse-text>

          <csc-open-basket-stop-editing-button
            return-url="${this.return_url}"
            basket-index-url="${this.basket_index_url}"
            basket-id="${this.basket_id}"
          >
          </csc-open-basket-stop-editing-button>
        </div>
        
        <div class="info-block">
          <cse-text as="h5" variant="heading-5">
            ${t('edit_mode_info_title', {
              ns: 'csc-open-basket-index-banner',
              default: 'Basket you are editing:'
            })}
          </cse-text>
          
          <cse-text as="span" variant="body">
            ${t('edit_mode_info_user_party_name', {
              ns: 'csc-open-basket-index-banner',
              default: 'Party: {{variable_party_name}}',
              variable_party_name: userInfo.party_name,
            })}
          </cse-text>

          <div class="stack">
            <cse-text as="span" variant="body">
              ${t('edit_mode_info_user_name', {
                ns: 'csc-open-basket-index-banner',
                default: 'User: {{variable_name}}',
                variable_name: userInfo.name,
              })}
            </cse-text>

            <cse-text as="span" variant="body">
              ${t('edit_mode_info_user_email', {
                ns: 'csc-open-basket-index-banner',
                default: 'User e-mail: {{variable_email}}',
                variable_email: userInfo.email,
              })}
            </cse-text>
            
            <cse-text as="span" variant="body">
              ${t('edit_mode_info_user_basket_name', {
                ns: 'csc-open-basket-index-banner',
                default: 'Basket name: {{variable_basket_name}}',
                variable_basket_name: userInfo.basketName,
              })}
            </cse-text>
          </div>
        </div>
    `
  }
}


@customElement('csc-open-basket-top-banner')
export class CSCOpenBasketTopBanner extends OpenBasketBannerBase {
  override render(): TemplateResult {
    const userInfo = {
      name: this.user_info ? this.getUserName(this.user_info.user_name) : 'Unknown',
      email: this.user_info?.email || '',
    }

    const htmlInTransTrickery = t('edit_mode_description', {
      ns: 'csc-open-basket-top-banner',
      default: 'You are now editing the basket of {{variable_user}}. Be sure to communicate any changes with the user.',
      variable_user: userInfo.name,
    }).toString().replace(userInfo.name, `<cse-text as="span" variant="heading-5">${userInfo.name}</cse-text>`)

    return html`
      <cse-banner variant="warning">
        <cse-text as="p" variant="body">
          ${unsafeHTML(htmlInTransTrickery)}

          <csc-open-basket-stop-editing-button
            return-url="${this.return_url}"
            basket-index-url="${this.basket_index_url}"
            basket-id="${this.basket_id}"
            variant="link"
          ></csc-open-basket-stop-editing-button>
        </cse-text>

        <cse-icon slot="icon" icon-role="csc-open-basket-top-banner.title"></cse-icon>
      </cse-banner>
    `
  }
}
